import { useCallback, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { SHOWREEL } from "@/data/site";
import { FeaturedPlayer } from "@/components/VideoPreview";
import { EASE, viewport } from "@/lib/motion";

const poster = `https://i.ytimg.com/vi/${SHOWREEL.youtubeId}/maxresdefault.jpg`;

export const Showreel = ({ onBrief }) => {
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const onFail = useCallback(() => {
    setFailed(true);
    setPlaying(false);
  }, []);

  return (
    <section
      id="reel"
      data-testid="showreel"
      className="relative border-t border-[#1f1f1f] bg-[#050505] py-14 md:py-20"
    >
      <div className="mx-auto max-w-[1700px] px-5 md:px-10">
        <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="label text-[#d4ff00]">{SHOWREEL.label}</span>
            <h2 className="mt-3 font-display text-3xl font-black uppercase leading-[0.9] tracking-[-0.04em] text-[#e5e5e5] md:text-5xl">
              {SHOWREEL.heading}
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-[#a3a3a3]">
            Short-form, creator content, brand and product edits, promos and
            spec commercials — cut by HSK for clients worldwide.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewport}
          transition={{ duration: 1, ease: EASE }}
          className="relative aspect-video w-full overflow-hidden border border-[#1f1f1f] bg-[#0a0a0a]"
          data-testid="showreel-frame"
        >
          {playing ? (
            <FeaturedPlayer id={SHOWREEL.youtubeId} onFail={onFail} />
          ) : (
            <button
              type="button"
              onClick={() => {
                if (failed) window.open(SHOWREEL.link, "_blank", "noreferrer");
                else setPlaying(true);
              }}
              data-testid="showreel-play"
              data-cursor="play"
              aria-label="Play featured edit"
              className="group absolute inset-0 block w-full outline-none focus-visible:ring-1 focus-visible:ring-[#d4ff00]"
            >
              <img
                src={poster}
                alt="HSK featured edit — poster frame"
                onError={(e) => {
                  if (!e.currentTarget.dataset.fallback) {
                    e.currentTarget.dataset.fallback = "1";
                    e.currentTarget.src = poster.replace(
                      "maxresdefault",
                      "hqdefault"
                    );
                  }
                }}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/80 via-[#0a0a0a]/20 to-[#0a0a0a]/40" />
              <div className="scanlines pointer-events-none absolute inset-0 opacity-30" />
              <span className="pointer-events-none absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center border border-[#d4ff00] bg-[#0a0a0a]/50 text-[#d4ff00] backdrop-blur transition-colors duration-300 group-hover:bg-[#d4ff00] group-hover:text-[#0a0a0a] md:h-20 md:w-20">
                <Play size={18} />
              </span>
              <span className="pointer-events-none absolute bottom-4 left-4 label border border-[#e5e5e5]/25 bg-[#0a0a0a]/70 px-3 py-2 text-[#e5e5e5] backdrop-blur md:bottom-6 md:left-6">
                {failed ? "WATCH ON YOUTUBE ↗" : "PLAY FEATURED EDIT"}
              </span>
            </button>
          )}

          <div className="hidden">
          </div>
        </motion.div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <a
            href={SHOWREEL.link}
            target="_blank"
            rel="noreferrer"
            data-cursor="play"
            data-testid="showreel-watch-youtube"
            className="group flex items-center gap-3"
          >
            <span className="label text-[#d4ff00]">WATCH PROJECT ↗</span>
            <span className="h-[1px] w-8 bg-[#d4ff00]/40 transition-all duration-500 group-hover:w-14 group-hover:bg-[#d4ff00]" />
          </a>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="#work"
              data-cursor="view"
              data-testid="showreel-view-work"
              className="border border-[#262626] px-4 py-3 label text-[#a3a3a3] transition-colors duration-300 hover:border-[#a3a3a3] hover:text-[#e5e5e5]"
            >
              VIEW SELECTED WORK ↓
            </a>
            <button
              type="button"
              onClick={onBrief}
              data-testid="showreel-start-project"
              className="border border-[#d4ff00] px-4 py-3 label text-[#d4ff00] transition-colors duration-300 hover:bg-[#d4ff00] hover:text-[#0a0a0a]"
            >
              START A PROJECT →
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 border-t border-[#1f1f1f] pt-5 md:grid-cols-4">
          {[
            ["EDITS FOR", "CREATORS + BRANDS"],
            ["FORMATS", "9:16 / 4:5 / 16:9"],
            ["AVAILABILITY", "WORLDWIDE"],
            ["WORKING", "WITH CLIENTS WORLDWIDE"],
          ].map(([k, v]) => (
            <div key={k}>
              <span className="label text-[#7a7a7a]">{k}</span>
              <p className="mt-1.5 text-xs text-[#e5e5e5] md:text-sm">{v}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
